import { styled } from '@mui/material/styles';
import React from 'react';

import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import SharedDataGrid from 'components/data-grid';

type Props = React.ComponentProps<typeof SharedDataGrid>;

// Comfortable density scales heights by 1.3. Keep the header at its current 52px and render rows at 32px.
const GRID_ROW_HEIGHT = 25;
const GRID_HEADER_HEIGHT = 40;

const StyledDataGrid = styled(SharedDataGrid, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  '&.MuiDataGrid-root': {
    border: `1px solid ${chartPalette.borders.subtle}`,
    backgroundColor: chartPalette.backgrounds.surface,
    '& svg': { color: chartPalette.text.secondary },
  },
  '& .MuiDataGrid-columnHeaders': {
    '& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--pinnedRight':
      { backgroundColor: `${chartPalette.backgrounds.sunken} !important` },
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: chartPalette.backgrounds.sunken,
    color: chartPalette.text.primary,
    borderRight: `1px solid ${chartPalette.borders.subtle}`,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    color: chartPalette.text.primary,
  },
  '& .MuiDataGrid-cell': {
    borderTop: 'none',
    borderRight: `1px solid ${chartPalette.borders.subtle}`,
    color: chartPalette.text.primary,
    fontSize: 14,
    '&.cell--disabled': { backgroundColor: `${chartPalette.backgrounds.sunken} !important` },
  },
  '& .MuiDataGrid-scrollbar': {
    backgroundColor: chartPalette.backgrounds.sunken,
    scrollbarColor: `${chartPalette.text.secondary} ${chartPalette.backgrounds.sunken}`,
    scrollbarWidth: 'thin',
    '&.MuiDataGrid-scrollbar--vertical::-webkit-scrollbar': { width: 14 },
    '&.MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar': { height: 14 },
    '&::-webkit-scrollbar-track': { backgroundColor: chartPalette.backgrounds.sunken },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: chartPalette.text.secondary,
      border: `3px solid ${chartPalette.backgrounds.sunken}`,
      borderRadius: 7,
    },
  },
  '& .MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--borderTop': {
    borderTop: 'none',
  },
  '& .MuiDataGrid-row': {
    backgroundColor: chartPalette.backgrounds.surface,
    '&:hover': { backgroundColor: chartPalette.backgrounds.raised },
    '&.even': { backgroundColor: chartPalette.backgrounds.surface },
    '&.odd': { backgroundColor: chartPalette.backgrounds.sunken },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${chartPalette.borders.subtle}`,
    backgroundColor: chartPalette.backgrounds.surface,
    color: chartPalette.text.secondary,
  },
  '& .MuiDataGrid-overlay': {
    backgroundColor: 'transparent',
    color: chartPalette.text.secondary,
  },
}));

// Adapted from the Data Lab grid so the table follows the active chart palette.
const BenchmarkDataGrid: React.FC<Props> = (props) => {
  const chartPalette = useChartPalette();

  return (
    <StyledDataGrid
      chartPalette={chartPalette}
      {...props}
      rowHeight={GRID_ROW_HEIGHT}
      columnHeaderHeight={GRID_HEADER_HEIGHT}
    />
  );
};

export default BenchmarkDataGrid;
